import { api } from './client';
import type { PAList } from './money';

/**
 * ============================================================================
 * THE HELPDESK CLIENT — §26 «Техническая поддержка» (F26, D215(b))
 * ============================================================================
 *
 *   GET  /pa/tickets?offset=                       — my tickets, newest first
 *   GET  /pa/tickets/classes                       — the LIVE request classes
 *   POST /pa/tickets                               — open one (201 + the whole detail)
 *   GET  /pa/tickets/{id}                          — the ticket, its thread and its files
 *   POST /pa/tickets/{id}/messages                 — reply (201 + the posted message)
 *   POST /pa/tickets/{id}/files                    — attach to the TICKET (multipart)
 *   POST /pa/tickets/{id}/messages/{mid}/files     — attach to ONE reply (multipart)
 *   GET  /pa/tickets/{id}/files/{file_id}          — the bytes (never built here)
 *
 * It mirrors the wire structs in `backend/internal/personalarea/tickets_handlers.go`,
 * pinned BOTH WAYS to `api/openapi.yaml` by `src/api/wire-spec.test.ts`.
 *
 * ============================================================================
 * ⚠ FOUR RULES THIS MODULE AND ITS SCREENS LIVE UNDER
 * ============================================================================
 *
 * 1. **THERE IS NO `contract_id` ANYWHERE ON THIS SURFACE**, and that is the one thing
 *    that makes it different from every other authenticated read in this app. A ticket
 *    hangs off `ticket.subscriber_id`; §2.16 has no contract in it. The scope is the
 *    DOMAIN's — `tickets.ListForSubscriber` forces the subscriber id and excludes archived
 *    rows whatever a filter says, and the actor is built from the SESSION — so a client
 *    that added a contract would be inventing a link the schema does not carry, and a
 *    two-contract subscriber would see his tickets split between two pickers for no
 *    reason. `wire-spec.test.ts` pins the absence, because an ignored query key looks like
 *    a filter that found everything.
 *
 * 2. **`status.allow_reply` IS THE SERVER'S VERDICT AND THE SCREEN NEVER RE-DERIVES IT.**
 *    Appendix M §2.1 calls it the only thing that ever stops a subscriber writing — and a
 *    «Done» status does NOT stop him, so `is_done` is not the predicate. The write answers
 *    `409 pa.ticket_reply_not_allowed`, so a form offered off the wrong field is a form
 *    that fails on Send.
 *
 * 3. **`download_url` IS FOLLOWED, NEVER BUILT.** The server publishes an API path on this
 *    listener; composing one from ids would be a client deciding which ids are reachable,
 *    which is the enumeration the flat `404 pa.not_found` exists to prevent.
 *    `TicketScreen.test.tsx` sweeps the sources for a composed attachment path.
 *
 * 4. **NO AUTHOR NAME EXISTS ON THIS WIRE.** A message carries `author_kind` («subscriber»
 *    / «staff» / «system») and no display name: §26b's own screenshot shows «Администратор»
 *    — a label, not a person — and this listener faces the internet. The portal owns the
 *    Russian words.
 *
 * ⚠ AND WHAT IS DELIBERATELY ABSENT FROM THE REQUESTS: a STATUS (§26b draws it as
 * «dropdown (display)» and no status crosses this wire), a «Служебное» flag (an internal
 * message from a subscriber is refused by the database, the kernel and the service — this
 * module not carrying the field is the fourth and cheapest refusal), a «Копировать
 * менеджерам» list, and an attachment DESCRIPTION (`stored_file` has no label column, so
 * the FILENAME is the label — Appendix L §2.16, struck in place).
 */

/**
 * TICKET_PAGE_SIZE mirrors `ticketPageMax` — the server's page, not the caller's: there is
 * no `?limit=` on this surface. Pinned to the Go constant by `src/api/page-size.test.ts`,
 * because every fixture in the suite is smaller than one page, so a drifted mirror would
 * skip or repeat rows only on a real estate.
 */
export const TICKET_PAGE_SIZE = 100;

/** `PATicketStatus` — the status as its subscriber sees it. */
export interface PATicketStatus {
  id: number;
  name: string;
  /** The dictionary's own tint, so the portal invents no palette for a renamed status. */
  color: string;
  is_done: boolean;
  /**
   * ⚠ THE SERVER'S OWN VERDICT on whether he may still write — see rule 2. Never
   * `!is_done`: a «Выполнен» status may still admit a reply and an «В процессе» one may
   * not, which is exactly why this field is published.
   */
  allow_reply: boolean;
}

/** `PATicketClass` — a request class (§26a «Класс запроса»). */
export interface PATicketClass {
  id: number;
  name: string;
  color: string;
}

/** `PATicketClasses` — the LIVE classes a subscriber may choose. An ARCHIVED class is
 *  still rendered on the old ticket that carries it and is never offered here. */
export interface PATicketClasses {
  items: PATicketClass[];
  total: number;
}

/** `PATicket` — one row of §26's «Запросы». */
export interface PATicket {
  id: number;
  subject: string;
  /** «subscriber» — he raised it; «staff» — the front desk recorded his call (§2.16). */
  origin: string;
  status: PATicketStatus;
  /** Null when the ticket carries no class. */
  class: PATicketClass | null;
  created_at: string;
  updated_at: string;
}

/** `PATicketMessage` — one entry of §26b's correspondence block. See rule 4. */
export interface PATicketMessage {
  id: number;
  /** «subscriber» | «staff» | «system» — a KIND, never a name. */
  author_kind: string;
  created_at: string;
  /** Non-null when staff corrected the text after posting, so the thread does not
   *  silently change under him. */
  edited_at: string | null;
  body: string;
}

/** `PAAttachment` — one file on a ticket or on one message of its thread. */
export interface PAAttachment {
  id: number;
  filename: string;
  /** The SNIFFED size and type, never the uploader's claim. */
  size_bytes: number;
  content_type: string;
  uploaded_by_kind: string;
  uploaded_at: string;
  /** The thread entry this file hangs off, or null when it is on the TICKET itself. */
  message_id: number | null;
  /** An API path on THIS listener. FOLLOWED, never built — see rule 3. */
  download_url: string;
}

/**
 * `PATicketDetail` — §26b in ONE read: the ticket, its whole thread and its attachments.
 *
 * ⚠ ITS FIRST SEVEN FIELDS ARE `PATicket`'s, RESTATED RATHER THAN INHERITED. The Go wire
 * type embeds `paTicketWire` and `encoding/json` FLATTENS an embedded struct, so the spec
 * declares one flat property set and `tsInterfaceFields` reads only an interface's OWN
 * lines. An `extends PATicket` here would leave the two-way pin unable to see the promoted
 * fields — which is the shape that lets a renamed tag round-trip green on both ends.
 */
export interface PATicketDetail {
  id: number;
  subject: string;
  origin: string;
  status: PATicketStatus;
  class: PATicketClass | null;
  created_at: string;
  updated_at: string;
  messages: PATicketMessage[];
  attachments: PAAttachment[];
  /**
   * The per-file ceiling in force on this installation, so the form can refuse a 40 MB
   * video before spending the subscriber's uplink on it — and so the number on screen is
   * the server's own rather than a hard-coded guess. 0 when attachments are unavailable.
   */
  attachment_max_bytes: number;
  /**
   * FALSE on an installation with no blob storage. The form then renders NO picker at all
   * — the same rule `notifier_available` follows on the profile screen, and the one §26a
   * states in as many words («hides the picker entirely»).
   */
  attachments_available: boolean;
}

/** `PATicketCreateRequest` — §26a's form. */
export interface PATicketCreateRequest {
  /**
   * Optional on the WIRE (an installation with no live class must still be able to open a
   * ticket) and required on the SCREEN when the operator has configured any. An id that is
   * not a LIVE class is REFUSED (`pa.ticket_class_unknown`), never dropped to «no class»:
   * a subscriber who chose «Ошибка» and got an unclassified ticket is routed to the wrong
   * queue and nothing tells either party.
   */
  class_id: number | null;
  subject: string;
  body: string;
}

/** `PATicketReplyRequest` — §26b's «Новое сообщение», and nothing else. */
export interface PATicketReplyRequest {
  body: string;
}

// ---- calls ------------------------------------------------------------------

/**
 * listTickets reads one page. `total` in the envelope is the SERVER's count over the whole
 * set and never `items.length`, which is what lets the pager say «Всего записей: 812» on a
 * hundred-row page instead of showing a truncated list as if it were complete.
 */
export function listTickets(offset = 0): Promise<PAList<PATicket>> {
  const q = new URLSearchParams();
  if (offset) q.set('offset', String(offset));
  const query = q.toString();
  return api.get<PAList<PATicket>>(`/pa/tickets${query ? `?${query}` : ''}`);
}

export function getTicketClasses(): Promise<PATicketClasses> {
  return api.get<PATicketClasses>('/pa/tickets/classes');
}

/**
 * createTicket opens one and returns the WHOLE detail, which is what the server answers.
 *
 * `class_id` is sent as `null` when the installation offers no class — explicitly, not by
 * omission, because the field is the one thing that routes his request and «absent» and
 * «none» must not be two spellings a later reader has to tell apart.
 */
export function createTicket(input: PATicketCreateRequest): Promise<PATicketDetail> {
  return api.post<PATicketDetail>('/pa/tickets', input);
}

export function getTicket(id: number): Promise<PATicketDetail> {
  return api.get<PATicketDetail>(`/pa/tickets/${id}`);
}

/** replyToTicket posts one message and returns THAT message — not the fresh thread. */
export function replyToTicket(id: number, body: string): Promise<PATicketMessage> {
  const payload: PATicketReplyRequest = { body };
  return api.post<PATicketMessage>(`/pa/tickets/${id}/messages`, payload);
}

/**
 * attachToTicket uploads ONE file against the ticket itself — §26a's picker.
 *
 * ⚠ THE PART IS NAMED `file` AND THERE IS EXACTLY ONE. Every other part is drained and
 * discarded by the server, including §26a's «Описание», which has nowhere to be stored.
 */
export function attachToTicket(id: number, file: File): Promise<PAAttachment> {
  const form = new FormData();
  form.append('file', file);
  return api.upload<PAAttachment>(`/pa/tickets/${id}/files`, form);
}

/**
 * attachToMessage uploads ONE file against a reply — which is what §26b's own form draws
 * and what the administrative thread does, so a reply and its screenshot stay together.
 */
export function attachToMessage(id: number, messageID: number, file: File): Promise<PAAttachment> {
  const form = new FormData();
  form.append('file', file);
  return api.upload<PAAttachment>(`/pa/tickets/${id}/messages/${messageID}/files`, form);
}
